// Build-time image manifest. `vite-imagetools` (registered in vite.config.ts) transforms the
// `import.meta.glob` queries below into responsive srcsets at build time. Maps are keyed by
// filename stem (e.g. `albanos-home`, `images-food`, `loc-la-porte`) for lookup from <AppImage>.
//
// Widths — 220;440;600;960;1920 cover this site's assets: the top banner (~960w shown), the home
// hero (600w), the per-page sidebar PNGs (220w), and the location photos. sharp does not upscale,
// so small sources (the 40w figure-bug, 100w Facebook badge) simply cap at native width.
//
// Formats — AVIF as the primary `<source>` plus a same-format fallback for the `<img>`. The
// fallback query intentionally omits `format` so the source format is preserved (jpg stays jpg,
// png stays png with alpha — load-bearing for the transparent sidebar/hero PNGs).
//
// Query form — the literal string query is load-bearing. Passing widths through the object-form
// `query: { w: '220;440;...' }` makes Vite URL-encode the value, and imagetools-core then emits
// only one width variant instead of splitting on `;`. String queries are preserved verbatim.
const avifMap = import.meta.glob<string>('../images/*.{jpg,jpeg,png}', {
  query: '?w=220;440;600;960;1920&format=avif&quality=85&as=srcset',
  import: 'default',
  eager: true,
});

const fallbackMap = import.meta.glob<string>('../images/*.{jpg,jpeg,png}', {
  query: '?w=220;440;600;960;1920&quality=88&as=srcset',
  import: 'default',
  eager: true,
});

const metaMap = import.meta.glob<{ width: number; height: number }>('../images/*.{jpg,jpeg,png}', {
  query: '?w=1920&as=metadata',
  import: 'default',
  eager: true,
});

/**
 * A build-time-optimized image: an AVIF srcset, a same-format fallback srcset, and the intrinsic
 * dimensions of the largest variant (for aspect-ratio / CLS).
 */
export interface OptimizedImage {
  avif: string;
  fallback: string;
  width: number;
  height: number;
}

/**
 * Strip directory and extension from a glob path, leaving the filename stem used as the lookup key.
 */
function stem(path: string): string {
  const file = path.split('/').pop() ?? path;
  return file.replace(/\.[^.]+$/, '');
}

const registry: Record<string, OptimizedImage> = {};
for (const path of Object.keys(avifMap)) {
  const meta = metaMap[path];
  registry[stem(path)] = {
    avif: avifMap[path],
    fallback: fallbackMap[path],
    width: meta?.width ?? 0,
    height: meta?.height ?? 0,
  };
}

/**
 * Resolve a build-time-optimized image by its filename stem. Throws on an unknown key so a typo or
 * missing asset fails the build rather than silently rendering nothing.
 */
export function getImage(name: string): OptimizedImage {
  const img = registry[name];
  if (!img) throw new Error(`Unknown image key "${name}" — add the asset under src/images/.`);
  return img;
}
